import { useEffect, useImperativeHandle, useLayoutEffect, useRef, useState, type CSSProperties, type Ref } from "react";
import type { PublicMapResident } from "@/shared/contracts";
import { PAN_MS, prefersReducedMotion, tweenPan, type MapCamera } from "@/shared/map/mapCamera";
import { ResidentActor } from "../actors/ResidentActor";
import { nowLine, presenceGroup } from "../residentView";
import { MapPortrait } from "./MapResidentCard";
import { STAGE_ACTOR, STAGE_H, STAGE_W, stageFit, type StageCast, type StageSky } from "./planetStageModel";
import "./planet-stage.css";

/**
 * 「先逛逛」的插画星球（WORLD-ART-117，WA；I 10-16 认可 PLAN-WORLD-ART-117）：访客落地看到的第一层。
 * 底图是 r7k 的 WORLD-PLANET-BASE-01（昼、夜两张，I 审过），居民不画在图里：页面按此刻的公开状态挑好谁、站哪（planetStageModel 的 stageCast），
 * 这里把 6796 的 ResidentActor 叠在站位点上（不改它：睡着冒 z、脸暗一点；醒着轻轻待机；在路上走着；到访中四处看看、挂指南针；说不清睡没睡不演）。
 * - 铺法同 object-fit: cover（手机竖屏铺满、裁边），比 0.6 宽的屏按高度放下整张；四周和挪开露出来的地方垫同一张图放大模糊（不新起颜色）；
 * - 每位是一颗按钮，挂地图标记同一套属性（ps-pmap-marker、data-cluster＝「stage:宠物号」、data-pet-id）：点开、关上、还焦点、就近面板量位置都由地图那一套管；
 *   画多大跟着底图缩放（390 宽 48px），点按区不小于 44px；按脚底 y 排，下面的盖上面的，读屏、Tab 也照这个顺序；
 * - 天上左上一颗「远处的地球」（复用 OBS-SPACE-01 原图的透明缩略图）：点它去 3D 地球，大家在真实地球上住在哪；
 * - 开场（首访）：星球从宇宙里拉近落地，时长和原来 3D 开场一样；小角色和地球等落地以后再出来。减少动态时直接落地；
 * - 镜头口子（117-2b 的 MapCamera）：就近面板放不下生活照时，地图让舞台整体往上挪一点；有限度，挪到头由地图收档。卡关上舞台回原处；
 * - 底部面板拉起来时舞台不跟着上移（3D 球会上移；插画上移会把远处的地球顶到顶栏底下，顶栏字也落进画里，设计评审 R1 阻断 1），
 *   这时舞台上的按钮不进 Tab 顺序、小角色停住；
 * - 昼夜换了：新的那张淡入盖上旧的（减少动态时只留进场淡入，data-motion=enter）；还说不准昼夜（读着、都没读出来）时不画星球，只留宇宙底。
 * 小角色在面板、抽屉盖着、舞台在挪、开场途中都停住（paused，6796 口径）；外面不再包带 transform 动画的容器。
 */

const ART: Record<StageSky, string> = {
  day: "/ui-assets/WORLD-PLANET-BASE-01/v1/planet-day.webp",
  night: "/ui-assets/WORLD-PLANET-BASE-01/v1/planet-night.webp",
};
const EARTH = "/ui-assets/OBS-SPACE-01/perf117/planet-still-192-62d020cb.webp";
const EARTH_SRC_SET = `${EARTH} 192w, /ui-assets/OBS-SPACE-01/perf117/planet-still-384-6f1a2cfd.webp 384w`;
/** 远处的地球放在哪（底图逻辑坐标，球心）、多大：夜图的月亮、日图的太阳都在右上，左上这片天是空的。
 *  y 取 112：小字「地球上的家」落在夜图左边那团粉云上沿之上（设计评审 R1 中 7：原来 150，字压在云上）。
 *  按球心定位（不按整颗按钮的中心），样式里再保证球顶在顶栏下面（R2 中：320 宽球顶压到返回键 7px，390×640 按高度放下时压到品牌字） */
const EARTH_AT = { x: 58, y: 112, size: 56 } as const;
/** 开场拉近多久：和原来 3D 开场一样（PublicWorldMap 的 SPACE_AUTO_MS） */
export const STAGE_ARRIVE_MS = 1600;
/** 昼夜换图时旧的那张留多久（新的淡入盖上去） */
const SKY_FADE_MS = 650;

export type PlanetStageProps = {
  cast: readonly StageCast[];
  /** 昼还是夜；null＝还说不准（不画星球） */
  sky: StageSky | null;
  /** 名单上一共几位（读屏：画面里几位、一共几位） */
  total: number;
  /** 开着就近面板的那一位 */
  openPetId: string | null;
  /** 面板、抽屉盖着舞台：小角色停住 */
  paused: boolean;
  /** 首访开场：从宇宙拉近落地 */
  arriving: boolean;
  /** 落地了（开场放完、跳过、减少动态、不放开场）：小角色和地球才出来 */
  landed: boolean;
  /** 读完了还是说不准昼夜：不等了，直接当落地 */
  settled: boolean;
  onArrived: () => void;
  onPick: (resident: PublicMapResident, from: HTMLElement) => void;
  onEarth: () => void;
  camera?: Ref<MapCamera | null>;
};

/**
 * 底部「今天星球上发生的事」拉起来没有（看 .ps-daily-sheet 有没有 is-open）：拉起来时盖住舞台下半截，舞台上的按钮不进 Tab 顺序、小角色停住
 * （设计评审 R1：320 下被面板盖住的两位还能 Tab 到；6796 口径：面板盖住舞台时 paused）。手指照样点得到露在面板上面的那几位。
 */
function useSheetOpen(rootRef: { current: HTMLElement | null }): boolean {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const host = rootRef.current?.closest(".ps-pmap");
    if (!host || typeof MutationObserver === "undefined") return;
    const read = () => setOpen(Boolean(host.querySelector(".ps-daily-sheet.is-open")));
    read();
    const watch = new MutationObserver(read);
    watch.observe(host, { subtree: true, childList: true, attributes: true, attributeFilter: ["class"] });
    return () => watch.disconnect();
  }, [rootRef]);
  return open;
}

type Box = { w: number; h: number };

export function PlanetStage({ cast, sky, total, openPetId, paused, arriving, landed, settled, onArrived, onPick, onEarth, camera }: PlanetStageProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const sheetOpen = useSheetOpen(rootRef);
  const [box, setBox] = useState<Box | null>(null);
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const measure = () => {
      const rect = root.getBoundingClientRect();
      setBox((old) => (old && old.w === rect.width && old.h === rect.height ? old : { w: rect.width, h: rect.height }));
    };
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(root);
    window.addEventListener("resize", measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);
  // 量不到大小（还没排版、藏着）时先按底图本身的 390×844 排，量到了再换；不让舞台空着
  const fit = stageFit(box?.w || STAGE_W, box?.h || STAGE_H);
  const plateW = fit ? STAGE_W * fit.scale : 0;
  const plateH = fit ? STAGE_H * fit.scale : 0;

  // 镜头口子：整块舞台平移；上下左右各有限度（往上最多挪出底图六成高，往下三成，左右各四分之一宽），挪到头由地图收档
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const panRef = useRef(pan);
  panRef.current = pan;
  const limitRef = useRef({ w: 0, h: 0 });
  limitRef.current = { w: plateW, h: plateH };
  const stopPan = useRef<() => void>(() => {});
  const [moving, setMoving] = useState(false);
  const moveTimer = useRef(0);
  const clamp = (x: number, y: number) => {
    const { w, h } = limitRef.current;
    return { x: Math.max(-w * 0.25, Math.min(w * 0.25, x)), y: Math.max(-h * 0.6, Math.min(h * 0.3, y)) };
  };
  const panTo = (dx: number, dy: number, instant: boolean) => {
    stopPan.current();
    window.clearTimeout(moveTimer.current);
    if (!instant) {
      setMoving(true);
      moveTimer.current = window.setTimeout(() => setMoving(false), PAN_MS + 40);
    }
    stopPan.current = tweenPan((x, y) => setPan((p) => clamp(p.x + x, p.y + y)), dx, dy, instant);
  };
  // reach（117-2b 评审 R2 起镜头口子的可选项）：被点的那一位最多还能往上挪多少——往上的限度减去已经挪了的，地图只按这个量排档
  useImperativeHandle(camera, () => ({
    panBy: (dx: number, dy: number, instant: boolean) => panTo(dx, dy, instant),
    reach: () => Math.max(0, limitRef.current.h * 0.6 + panRef.current.y),
  }), []);
  useEffect(() => () => {
    stopPan.current();
    window.clearTimeout(moveTimer.current);
  }, []);
  // 卡关上：舞台回原处（地图那边挪了是让开照片，星球是一整幅画，不停在半路）
  useEffect(() => {
    if (openPetId) return;
    const { x, y } = panRef.current;
    if (x || y) panTo(-x, -y, prefersReducedMotion());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openPetId]);

  // 昼夜：换了以后旧的那张留一会儿，新的淡入盖上去
  const [shown, setShown] = useState<StageSky[]>(() => (sky ? [sky] : []));
  useEffect(() => {
    if (!sky) return;
    setShown((old) => (old[old.length - 1] === sky ? old : [...old.filter((s) => s !== sky), sky]));
    const timer = window.setTimeout(() => setShown((old) => (old.length > 1 ? old.slice(-1) : old)), SKY_FADE_MS);
    return () => window.clearTimeout(timer);
  }, [sky]);
  const art = shown[shown.length - 1] ?? null;

  // 开场：星球画出来就放拉近，放完算落地；减少动态直接落地；读完了还说不准昼夜（不画星球）也直接落地。
  // 读得慢、一直画不出来时最多也只在宇宙里停这么久：先落地（顶栏、面板、「正在看大家在哪」出来），星球读到了再淡入
  const drawn = Boolean(fit && art);
  // 星球第一次画出来时是不是正在开场：是的话拉近就是它的进场；不是（回访、开场时还没读到、先落了地）就淡入一下
  const drewArriving = useRef<boolean | null>(null);
  if (drawn && drewArriving.current === null) drewArriving.current = arriving && !landed;
  // 「说不准昼夜」看的是 sky，不是 drawn：舞台还没量到大小的那一下也画不出来，那不算说不准。只叫一次落地
  const arrivedRef = useRef(false);
  useEffect(() => {
    if (!arriving || landed || arrivedRef.current) return;
    const arrive = () => {
      arrivedRef.current = true;
      onArrived();
    };
    if (prefersReducedMotion() || (settled && !sky)) {
      arrive();
      return;
    }
    const timer = window.setTimeout(arrive, STAGE_ARRIVE_MS);
    return () => window.clearTimeout(timer);
  }, [arriving, landed, settled, sky, drawn, onArrived]);

  const actor = fit ? Math.max(36, Math.min(56, Math.round(STAGE_ACTOR * fit.scale))) : STAGE_ACTOR;
  const hold = paused || moving || !landed || sheetOpen;
  const others = total - cast.length;
  const earthPx = fit ? Math.max(48, Math.round(EARTH_AT.size * fit.scale)) : EARTH_AT.size;
  // 同一幅原画的透明缩略图；预读和落地后的 img 使用完全相同的尺寸选择，避免先下大图或重复下两档。
  // 把带宽优先留给整张星球，原开场/落地时钟、点击及还焦点不变。
  const earthRef = useRef<HTMLImageElement | null>(null);
  useEffect(() => {
    if (typeof Image === "undefined") return;
    const img = new Image();
    img.fetchPriority = "low";
    img.decoding = "async";
    img.sizes = `${earthPx}px`;
    img.srcset = EARTH_SRC_SET;
    img.src = EARTH;
    earthRef.current = img;
  }, [earthPx]);

  return (
    <div ref={rootRef} className="ps-pmap__layer ps-stage" data-layer="stage" data-testid="planet-stage" data-sky={art ?? undefined} data-cover={fit?.cover ? "" : undefined}>
      {fit && art ? (
        <>
          {/* 垫底（同一张图放大模糊）只在落地以后：拉近途中四周是宇宙，不是一团糊开的星球（设计评审 R1 高 3） */}
          {arriving && !landed ? null : <div className="ps-stage__haze" aria-hidden="true" style={{ backgroundImage: `url("${ART[art]}")` }} />}
          <div className="ps-stage__pan" style={{ transform: pan.x || pan.y ? `translate(${Math.round(pan.x)}px, ${Math.round(pan.y)}px)` : undefined }}>
            <div className="ps-stage__plate" data-arriving={arriving && !landed ? "" : undefined} data-motion={drewArriving.current ? undefined : "enter"} style={{ left: fit.left, top: fit.top, width: plateW, height: plateH, "--stage-arrive": `${STAGE_ARRIVE_MS}ms` } as CSSProperties}>
              {shown.map((s) => (
                <img key={s} className="ps-stage__art" src={ART[s]} alt="" draggable={false} decoding="async" fetchPriority={s === art ? "high" : "auto"} data-sky={s} data-motion={shown.length > 1 && s === art ? "enter" : undefined} />
              ))}
              {landed ? (
                <button type="button" className="ps-stage__earth" data-map-interactive data-motion="enter" onClick={onEarth} tabIndex={sheetOpen ? -1 : undefined}
                  aria-label="地球上的家：去看看大家在真实地球上住在哪"
                  style={{ left: EARTH_AT.x * fit.scale, "--earth-y": `${EARTH_AT.y * fit.scale}px`, "--stage-earth": `${earthPx}px` } as CSSProperties}>
                  <img src={EARTH} srcSet={EARTH_SRC_SET} sizes={`${earthPx}px`} width={192} height={192} alt="" draggable={false} decoding="async" fetchPriority="low" />
                  <span className="ps-stage__earth-name" aria-hidden="true">地球上的家</span>
                </button>
              ) : null}
              {landed && cast.length ? (
                <ul className="ps-stage__cast" aria-label={`星球此刻：画面里 ${cast.length} 位`}>
                  {cast.map(({ resident, point }) => {
                    const id = resident.pet_id;
                    // 同名的名字后面已经带了城市（withCityOnSameName，如「栗子（西安）」）就不再说一遍
                    const city = resident.city?.trim() && !resident.name.endsWith(`（${resident.city.trim()}）`) ? resident.city.trim() : null;
                    return (
                      <li key={id} className="ps-stage__spot" data-point={point.id} data-place={point.place} data-motion="enter"
                        style={{ left: point.x * fit.scale, top: point.y * fit.scale, "--stage-actor": `${actor}px` } as CSSProperties}>
                        <button type="button" className="ps-pmap-marker ps-stage__actor" data-map-interactive data-pet-id={id} data-cluster={`stage:${id}`}
                          data-zone={presenceGroup(resident.presence)} aria-haspopup="dialog" aria-expanded={openPetId === id} tabIndex={sheetOpen ? -1 : undefined}
                          aria-label={`${resident.name}：${nowLine(resident)}${city ? `，在${city}` : ""}`}
                          onClick={(event) => {
                            event.currentTarget.focus({ preventScroll: true });
                            onPick(resident, event.currentTarget);
                          }}>
                          <ResidentActor resident={resident} size={actor} paused={hold}
                            portrait={<span className="ps-stage__face" aria-hidden="true"><MapPortrait resident={resident} size={actor} /></span>} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </div>
          </div>
          {landed && others > 0 ? <p className="visually-hidden">星球上一共 {total} 位，画面里没有的在下面「今天星球上发生的事」里。</p> : null}
        </>
      ) : null}
    </div>
  );
}
